/**
 * Pro free-trial banner — shown on the Dashboard when a user is on a
 * 3-day Pro trial granted after logging their first catch.
 */
import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Crown, Sparkles, X } from 'lucide-react';

const formatRemaining = (endsAt) => {
  if (!endsAt) return null;
  const ms = new Date(endsAt).getTime() - Date.now();
  if (ms <= 0) return null;
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  const hours = Math.floor((ms % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  if (days >= 1) return `${days}d ${hours}h left`;
  const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
  if (hours >= 1) return `${hours}h ${minutes}m left`;
  return `${minutes}m left`;
};

const TrialBanner = ({ user }) => {
  const [dismissed, setDismissed] = useState(false);
  const [remaining, setRemaining] = useState(formatRemaining(user?.trial_ends_at));

  useEffect(() => {
    if (!user?.trial_active || !user?.trial_ends_at) return;
    const id = setInterval(() => setRemaining(formatRemaining(user.trial_ends_at)), 60_000);
    return () => clearInterval(id);
  }, [user?.trial_active, user?.trial_ends_at]);

  if (!user?.trial_active || !user?.trial_ends_at || dismissed) return null;
  if (!remaining) return null;

  return (
    <div
      data-testid="pro-trial-banner"
      className="relative mb-6 overflow-hidden rounded-lg border border-[#c8b273]/30 bg-gradient-to-r from-[#1a1d1a] via-[#141714] to-[#1a1d1a] p-5"
    >
      {/* Subtle gold radial glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-12 -right-12 h-40 w-40 rounded-full bg-[#c8b273]/10 blur-3xl"
      />
      <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#c8b273]/15">
            <Sparkles className="h-5 w-5 text-[#c8b273]" />
          </div>
          <div>
            <p className="data-label mb-1 text-[#c8b273]">Pro Trial Active</p>
            <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-wider text-white">
              You've unlocked the full Pro experience
            </h3>
            <p className="mt-1 text-sm text-[#8a948a]">
              Detailed lake intel, league access &amp; member perks — yours for the next {remaining}.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/subscription"
            data-testid="trial-keep-pro-btn"
            className="inline-flex items-center gap-2 rounded bg-[#c8b273] px-4 py-2 text-sm font-bold uppercase tracking-wide text-[#0b0d0b] transition-colors hover:bg-[#d8c283]"
          >
            <Crown className="h-4 w-4" />
            Keep Pro — £4.99/mo
          </Link>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            aria-label="Dismiss"
            className="rounded p-2 text-[#8a948a] transition-colors hover:bg-white/5 hover:text-white"
            data-testid="trial-banner-dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default TrialBanner;
